import React, { useEffect, useState, useRef } from 'react';
import { EnhancedProductCard } from '../shared/EnhancedProductCard';
import { PackageIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface BrandComboSetsProps {
  brandSlug: string;
}
export function BrandComboSets({
  brandSlug
}: BrandComboSetsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);
  const products = [{
    id: 13,
    name: 'Anti-Aging Routine Set',
    brand: 'Vichy',
    price: 399.99,
    originalPrice: 549.99,
    discount: 27,
    rating: 4.9,
    reviewCount: 567,
    image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=600',
    isBestSeller: true,
    expressDelivery: true
  }, {
    id: 14,
    name: 'Complete Hydration Kit',
    brand: 'Vichy',
    price: 299.99,
    originalPrice: 399.99,
    discount: 25,
    rating: 4.8,
    reviewCount: 445,
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=600',
    expressDelivery: true
  }, {
    id: 15,
    name: 'Sensitive Skin Care Bundle',
    brand: 'Vichy',
    price: 349.99,
    originalPrice: 469.99,
    discount: 26,
    rating: 4.7,
    reviewCount: 389,
    image: 'https://images.unsplash.com/photo-1471864190281-a93a3070b6de?w=600',
    expressDelivery: true
  }, {
    id: 16,
    name: 'Sun Care Family Pack',
    brand: 'Vichy',
    price: 279.99,
    originalPrice: 369.99,
    discount: 24,
    rating: 4.6,
    reviewCount: 312,
    image: 'https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=600',
    expressDelivery: true
  }];
  const itemsPerView = isMobile ? 2 : isTablet ? 3 : 6;
  const totalSlides = Math.ceil(products.length / itemsPerView);
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const slideWidth = scrollContainerRef.current.offsetWidth;
      const newSlide = Math.round(scrollLeft / slideWidth);
      setCurrentSlide(newSlide);
    }
  };
  const scrollToSlide = (index: number) => {
    if (scrollContainerRef.current) {
      const slideWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  };
  const scrollPrev = () => {
    const newSlide = Math.max(currentSlide - 1, 0);
    scrollToSlide(newSlide);
  };
  const scrollNext = () => {
    const newSlide = Math.min(currentSlide + 1, totalSlides - 1);
    scrollToSlide(newSlide);
  };
  return <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1315" data-mp="1v9xhwn3t5tt2">
      <div className="flex items-center gap-3 mb-6" data-id="element-1316" data-mp="1vbie0h5rtkt1">
        <div className="bg-purple-100 p-3 rounded-lg" data-id="element-1317" data-mp="1hjlnghjr3iyr">
          <PackageIcon className="w-6 h-6 text-purple-600" data-id="element-1318" data-mp="1hgpruzvdl6dh" />
        </div>
        <div data-id="element-1319" data-mp="1lkxfsuu0eian">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-1320" data-mp="1lkns5dwfsn84">
            Combo Sets & Bundles
          </h2>
          <p className="text-gray-600" data-id="element-1321" data-mp="2985hz3w55b8g">Save more with our curated sets</p>
        </div>
      </div>
      {/* Mobile/Tablet Slider */}
      {isMobile || isTablet ? <div className="relative" role="region" aria-label="Combo Sets and Bundles" data-id="element-1322" data-mp="208s84a43ukn7">
          <div ref={scrollContainerRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3 md:gap-4" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-1323" data-mp="25a7vi8tr938r">
            {products.map(product => <div key={product.id} className="snap-start flex-shrink-0" style={{
          width: isMobile ? 'calc(50% - 6px)' : 'calc(33.333% - 11px)'
        }} data-id="element-1324" data-mp="14abng7eecjy1">
                <EnhancedProductCard product={product} data-id="element-1325" data-mp="1s0o3y6npjp08" />
              </div>)}
          </div>
          {/* Navigation Arrows */}
          {totalSlides > 1 && <>
              <button onClick={scrollPrev} disabled={currentSlide === 0} className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed z-10" aria-label="Previous combo sets" data-id="element-1326" data-mp="1j09h32v3i1um">
                <ChevronLeftIcon className="w-6 h-6 text-gray-900" data-id="element-1327" data-mp="2eaedzh10y9l4" />
              </button>
              <button onClick={scrollNext} disabled={currentSlide === totalSlides - 1} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed z-10" aria-label="Next combo sets" data-id="element-1328" data-mp="2j98wsiyzry58">
                <ChevronRightIcon className="w-6 h-6 text-gray-900" data-id="element-1329" data-mp="2x305xq8t9g0f" />
              </button>
            </>}
          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-1330" data-mp="36zv5mcgtlmga">
            {Array.from({
          length: totalSlides
        }).map((_, index) => <button key={index} onClick={() => scrollToSlide(index)} className={`h-2 rounded-full transition-all ${currentSlide === index ? 'w-6 bg-[#1E2B55]' : 'w-2 bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1331" data-mp="1cksj8nn2ikhl" data-mpi={index} />)}
          </div>
        </div> /* Desktop Grid */ : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1332" data-mp="2f0b94jet526z">
          {products.map(product => <EnhancedProductCard key={product.id} product={product} data-id="element-1333" data-mp="2owe8xim6soqr" />)}
        </div>}
      <style jsx data-id="element-1334" data-mp="32w5ty3b4f5e9">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>;
}